/**
 * @file activeFilters.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-09-29
 * @modified 2026-09-29
 *
 * Which of a search screen's header filters are on — the search text, the
 * dropdowns, the date range, the toggles — measured against the state its
 * Clear Filters button resets to.
 *
 * Feeds two things every search screen shows the same way:
 *   - the Clear Filters button's `filters-applied` class (index.css), on while
 *     any filter of any kind is applied; and
 *   - ColumnFilterNote, the toolbar pill, which now names header filters too.
 *
 * Only keys present in `cleared` are compared, so a screen's own bookkeeping
 * in the same state object (page, limit) cannot count. Neither can the parts of
 * a date filter that filter nothing on their own: which date the range applies
 * to (dateType) and the preset's name (datePreset). From and To are one filter.
 */

const IGNORE = new Set(['page', 'limit', 'datePreset', 'dateType', 'sortField', 'sortDirection']);

const DATE_KEYS = new Set(['dateFrom', 'dateTo']);

const LABELS = {
  search: 'Search',
  date: 'Date',
  supplier: 'Supplier',
  customer: 'Customer',
  status: 'Status',
  productRange: 'Product range',
  archived: 'Archived',
  includeAdditives: '+ADD'
};

/** Blank-ish values are all "not set": '', null, undefined, []. */
const norm = (v) => {
  if (v === undefined || v === null || v === '') return null;
  if (Array.isArray(v)) return v.length ? JSON.stringify([...v].map(String).sort()) : null;
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
};

const humanise = (key) => key
  .replace(/_/g, ' ')
  .replace(/([a-z])([A-Z])/g, '$1 $2')
  .replace(/^./, (c) => c.toUpperCase());

/**
 * @param {object} filters  the screen's current filter state
 * @param {object} cleared  what its Clear Filters resets them to
 * @param {object} [opts]
 * @param {string[]} [opts.ignore]  more keys that are not filters
 * @param {object}   [opts.labels]  key → label for the toolbar pill
 * @returns {{ count: number, labels: string[] }}
 */
export function activeHeaderFilters(filters, cleared, { ignore = [], labels = {} } = {}) {
  const skip = new Set([...IGNORE, ...ignore]);
  const on = [];
  let dateOn = false;
  for (const key of Object.keys(cleared || {})) {
    if (skip.has(key)) continue;
    if (norm(filters?.[key]) === norm(cleared[key])) continue;
    if (DATE_KEYS.has(key)) { dateOn = true; continue; }
    on.push(labels[key] || LABELS[key] || humanise(key));
  }
  if (dateOn) on.push(labels.date || LABELS.date);
  return { count: on.length, labels: on };
}

/** The Clear Filters button's class list: `btn-slideout`, plus the flag. */
export const clearFiltersClass = (applied, base = 'btn-slideout') =>
  applied ? `${base} filters-applied` : base;
